import Typography from '@mui/material/Typography';
import { Control, useFieldArray } from 'react-hook-form';
import { CreateNoticeSchemaFormValues } from '../schema';
import { PerInvestorBreakdownEntry } from '../useCapitalActionNotice/types';
import { EmailOutlined, SaveAlt } from '@mui/icons-material';
import { CurrencyCode } from 'further-types/lp/vehicle';
import Avatar from 'further-ui/components/Avatar';
import { Column, Row } from 'further-ui/components/Layout';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import Switch from 'further-ui/components/Switch';
import { sumBy } from 'lodash';
import {
  numberToCurrencyString,
  numberToPercentageString,
} from 'further-ui/utils';
import useCapitalActionNoticePdf from 'hooks/data/capitalActionNotice/useCapitalActionNoticePdf';

type Props = {
  currencyLcy: CurrencyCode;
  control: Control<CreateNoticeSchemaFormValues>;
  data: Array<PerInvestorBreakdownEntry>;
  vehicleId: string;
  capitalActionId: string;
};

const PerInvestorBreakdown: React.FC<Props> = ({
  currencyLcy,
  data,
  vehicleId,
  capitalActionId,
  control,
}) => {
  const { downloadPdf, isDownloading } = useCapitalActionNoticePdf();

  const { update, fields } = useFieldArray({
    control,
    name: 'perInvestorBreakdown',
    keyName: 'fieldId',
  });

  const toggleEmailNotification = (investorId: string) => {
    const index = fields.findIndex((field) => field.id === investorId);
    if (index === -1) return;
    const data = fields[index];
    update(index, { ...data, notifyViaEmail: !data.notifyViaEmail });
  };

  const columns: Array<TableColumn<PerInvestorBreakdownEntry>> = [
    {
      label: 'Investor',
      key: 'id',
      render: ({ investorName }) => (
        <Row centered spacing="xs">
          <Avatar color="random" size="md" text={investorName} />
          <Typography variant="inherit">{investorName}</Typography>
        </Row>
      ),
      footer: `Total from ${data.length} investors`,
    },
    {
      label: 'Capital call',
      key: 'capitalCalledLcy',
      render: ({ capitalCalledLcy }) =>
        numberToCurrencyString(capitalCalledLcy, { currency: currencyLcy }),
      footer: numberToCurrencyString(sumBy(data, 'capitalCalledLcy'), {
        currency: currencyLcy,
      }),
    },
    {
      label: 'Distribution / capital refund',
      key: 'capitalDistributedLcy',
      render: ({ capitalDistributedLcy }) =>
        numberToCurrencyString(capitalDistributedLcy, {
          currency: currencyLcy,
        }),
      footer: numberToCurrencyString(sumBy(data, 'capitalDistributedLcy'), {
        currency: currencyLcy,
      }),
    },
    {
      label: 'Total due to / (from) fund',
      key: 'totalDueLcy',
      render: ({ totalDueLcy }) =>
        numberToCurrencyString(totalDueLcy, { currency: currencyLcy }),
      footer: numberToCurrencyString(sumBy(data, 'totalDueLcy'), {
        currency: currencyLcy,
      }),
    },
    {
      label: 'Unfunded commitment (post-notice)',
      key: 'unfundedCommitmentLcy',
      render: ({ unfundedCommitmentLcy, unfundedCommitmentPercentage }) => (
        <Column>
          <Typography variant="inherit">
            {numberToCurrencyString(unfundedCommitmentLcy, {
              currency: currencyLcy,
            })}
          </Typography>
          <Typography variant="body2" color="grey.600">
            {numberToPercentageString(unfundedCommitmentPercentage, {
              forceDecimals: true,
              accuracy: 2,
            })}
          </Typography>
        </Column>
      ),
      footer: numberToCurrencyString(sumBy(data, 'unfundedCommitmentLcy'), {
        currency: currencyLcy,
      }),
    },
    {
      label: 'Email notification',
      key: 'notifyViaEmail',
      render: ({ id, includedInNotice }) => (
        <Row centered justify="space-between">
          <Switch
            tooltip={
              includedInNotice
                ? undefined
                : 'This investor is not included in this capital action and will not be notified.'
            }
            edge="start"
            checked={
              includedInNotice
                ? Boolean(
                    fields.find((field) => field.id === id)?.notifyViaEmail,
                  )
                : false
            }
            disabled={!includedInNotice}
            onChange={() => {
              toggleEmailNotification(id);
            }}
          />
        </Row>
      ),
    },
  ];
  return (
    <Column spacing="sm">
      <Typography variant="h5">Per-investor breakdown</Typography>
      <Table
        rowHasPendingAction={(row) => isDownloading(row.id, capitalActionId)}
        emptyMessage="No investors"
        rows={data}
        actions={[
          {
            icon: SaveAlt,
            label: 'Download preview PDF',
            onClick: ({ id }) => {
              downloadPdf({ investorId: id, capitalActionId, vehicleId });
            },
            disabled: ({ includedInNotice }) => !includedInNotice,
            tooltip: ({ includedInNotice }) =>
              !includedInNotice
                ? 'This investor is not included in this capital action and will not be notified.'
                : undefined,
          },
          {
            icon: EmailOutlined,
            label: ({ id }) => {
              const fieldData = fields.find((field) => field.id === id);
              return fieldData?.notifyViaEmail
                ? 'Exclude from email notification'
                : 'Include in email notification';
            },
            tooltip: ({ includedInNotice }) =>
              !includedInNotice
                ? 'This investor is not included in this capital action and will not be notified.'
                : undefined,
            onClick: ({ id }) => {
              toggleEmailNotification(id);
            },
            disabled: ({ includedInNotice }) => !includedInNotice,
          },
        ]}
        columns={columns}
      />
    </Column>
  );
};

export default PerInvestorBreakdown;
